import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';

const TOOLS = [
	{ value: 'auto', label: 'Auto', description: 'Pick best tool for the URL' },
	{ value: 'yt-dlp', label: 'yt-dlp', description: 'Videos (YouTube, Reddit, etc.)' },
	{ value: 'gallery-dl', label: 'gallery-dl', description: 'Images (Instagram, Reddit, etc.)' },
] as const;

type ToolValue = (typeof TOOLS)[number]['value'];

interface DownloadToolPickerProps {
	onDownload: (tool?: string) => void;
	onClose: () => void;
	disabled?: boolean;
}

export function DownloadToolPicker({ onDownload, onClose, disabled }: DownloadToolPickerProps) {
	const [selected, setSelected] = useState<ToolValue>('auto');
	const ref = useRef<HTMLDivElement>(null);

	// Close on click outside
	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				onClose();
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [onClose]);

	// Close on Escape
	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === 'Escape') onClose();
		}
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [onClose]);

	return (
		<div
			ref={ref}
			className="absolute right-0 top-full mt-1 z-50 w-64 rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
		>
			<div className="px-2 py-1.5 text-xs font-mono text-muted-foreground">
				Select download tool
			</div>
			<div className="h-px bg-border my-1" />
			{TOOLS.map((tool) => (
				<button
					key={tool.value}
					type="button"
					onClick={() => setSelected(tool.value)}
					className={`w-full text-left px-2 py-1.5 rounded-sm cursor-pointer hover:bg-accent transition-colors ${
						selected === tool.value ? 'bg-accent' : ''
					}`}
				>
					<div className="flex items-center gap-2">
						<div className={`size-3 rounded-full border ${selected === tool.value ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />
						<div>
							<div className="font-mono text-sm">{tool.label}</div>
							<div className="text-xs text-muted-foreground">{tool.description}</div>
						</div>
					</div>
				</button>
			))}
			<div className="h-px bg-border my-1" />
			<div className="p-1">
				<Button
					size="sm"
					className="w-full text-xs font-mono"
					onClick={() => onDownload(selected === 'auto' ? undefined : selected)}
					disabled={disabled}
				>
					[DOWNLOAD]
				</Button>
			</div>
		</div>
	);
}
